import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import PageHero from "@/components/shared/PageHero";
import { usePageContent } from "@/hooks/usePageContent";
import { useSettings } from "@/context/SettingsContext";

function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

const pressReleases = [
  {
    date: "June 2026",
    title: "CashMyProperty.ai Crosses ₹500 Cr in Value Unlocked for Landowners",
    excerpt:
      "India's first AI-powered land monetization platform announces cumulative value unlocked milestone across 25+ states.",
    source: "Business Standard",
  },
  {
    date: "March 2026",
    title: "Govianu Group Launches AI-Powered Land Monetization Platform",
    excerpt:
      "The Bengaluru-based real estate consultancy unveils CashMyProperty.ai — a platform that uses AI to find the highest and best use of idle land.",
    source: "Economic Times",
  },
  {
    date: "January 2026",
    title: "CashMyProperty.ai Partners with 200+ Brands for Land Placement",
    excerpt:
      "Platform expands Brand Match Algorithm to include Domino's, Starbucks, Shell, Amazon, and 200+ national and international brands.",
    source: "Mint",
  },
  {
    date: "November 2025",
    title: "AI Revolution in Indian Real Estate: CashMyProperty.ai Analyzes 10,000+ Acres",
    excerpt:
      "The platform's HBU Engine has now analyzed over 10,000 acres of land across India, generating monetization roadmaps for landowners.",
    source: "The Hindu BusinessLine",
  },
];

const mediaCoverage = [
  {
    name: "Forbes India",
    title: "How AI is Transforming Land Monetization in India",
    date: "May 2026",
  },
  {
    name: "Inc42",
    title: "CashMyProperty.ai: The PropTech Startup Unlocking Idle Land",
    date: "April 2026",
  },
  {
    name: "YourStory",
    title: "From Farm to Fortune: This AI Platform Helps Landowners Monetize Without Selling",
    date: "February 2026",
  },
  {
    name: "Moneycontrol",
    title: "Govianu Group's AI Venture Targets ₹5,000 Cr by 2030",
    date: "December 2025",
  },
];

export default function PressPage() {
  const { getContent } = usePageContent('press');
  const { settings } = useSettings();
  const displayPhone = settings.general.primary_phone || '+91 90350 63455';
  const phoneRaw = settings.general.primary_phone_raw || '+919035063455';
  const heroBadge = stripHtml(getContent('press-hero-badge')) || 'Media';
  const heroSub = stripHtml(getContent('press-hero-sub')) || 'News, press releases, and media coverage about CashMyProperty.ai.';
  const pressHeading = stripHtml(getContent('press-releases-heading')) || 'Latest News';
  const coverageHeading = stripHtml(getContent('press-coverage-heading')) || 'In the News';
  const mediaHeading = stripHtml(getContent('press-media-heading')) || 'Media Inquiries';
  const ctaHeading = stripHtml(getContent('press-cta-heading')) || 'Stay Updated';
  const mediaEmail = stripHtml(getContent('press-media-email')) || 'press@cashmyproperty.ai';

  return (
    <PageLayout>
      <PageHero
        badge={heroBadge}
        title="Press &"
        highlight="Media Coverage"
        subtitle={heroSub}
        image="https://readdy.ai/api/search-image?query=Professional%20press%20media%20newsroom%20concept%2C%20multiple%20screens%20showing%20news%20headlines%2C%20microphones%20and%20cameras%2C%20dark%20modern%20studio%20environment%2C%20dramatic%20teal%20and%20amber%20lighting%2C%20cinematic%20journalism%20photography%2C%20India%20media%20setting%2C%20highly%20detailed&width=1800&height=800&seq=press-hero-01&orientation=landscape"
      />

      {/* Press Releases */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
              Press Releases
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
              {pressHeading}
            </h2>
          </div>
          <div className="space-y-6 max-w-4xl mx-auto">
            {pressReleases.map((p) => (
              <div
                key={p.title}
                className="p-6 md:p-8 rounded-2xl card-dark group hover:border-primary-500/40 transition-all duration-300"
              >
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span className="text-xs text-primary-600 font-medium">{p.date}</span>
                  <span className="text-xs text-foreground-500">{p.source}</span>
                </div>
                <h3 className="font-display font-semibold text-xl text-foreground-950 mb-2">
                  {p.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{p.excerpt}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Media Coverage */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
              Media Coverage
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
              {coverageHeading}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {mediaCoverage.map((m) => (
              <div
                key={m.title}
                className="p-6 rounded-2xl card-dark group hover:border-primary-500/40 transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 shrink-0">
                    <i className="ri-newspaper-line text-xl text-primary-500" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-950">{m.name}</div>
                    <div className="text-xs text-foreground-500">{m.date}</div>
                  </div>
                </div>
                <h3 className="font-display font-semibold text-lg text-foreground-950 mb-2">
                  {m.title}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Media Appearances — YouTube Videos */}
      <section className="py-24 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
              Media Appearances
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
              TV &amp; Video Features
            </h2>
            <p className="text-foreground-600">
              Watch Mahesh Govianu's expert insights on financial literacy and land monetization across leading Kannada news channels.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              {
                videoId: 'kp5kaYHkG8c',
                title: 'MARKET MANTRA — Financial Literacy Guide',
                channel: 'Samaya TV',
                date: '26 Feb 2012',
              },
              {
                videoId: 'Y7ZQnuXci44',
                title: 'SHARE PETE — Investment & Financial Guidance',
                channel: 'Udaya News',
                date: '2013',
              },
              {
                videoId: 'pP5MmIJWBlw',
                title: 'SHARE PETE — Financial Literacy Program',
                channel: 'Udaya News',
                date: '10 Sep 2014',
              },
              {
                videoId: 'Y2kxRr5Yfoo',
                title: 'SHARE PETE — Market Analysis & Investment Tips',
                channel: 'Udaya News',
                date: '03 Oct 2013',
              },
              {
                videoId: 'HazPJ10RuD8',
                title: 'SHARE PETE — Viewer Investment Guidance',
                channel: 'Udaya News',
                date: '2013',
              },
              {
                videoId: 'rprNKZ8VXAU',
                title: 'SHARE PETE — Investment Literacy (26 Sep 2013)',
                channel: 'Udaya News',
                date: '26 Sep 2013',
              },
            ].map((video, i) => (
              <a
                key={`${video.videoId}-${i}`}
                href={`https://www.youtube.com/watch?v=${video.videoId}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="group rounded-2xl card-dark overflow-hidden hover:border-primary-500/40 transition-all duration-300 flex flex-col"
              >
                <div className="relative aspect-video overflow-hidden bg-background-950">
                  <img
                    src={`https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg`}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-500/90 group-hover:bg-primary-500 group-hover:scale-110 transition-all duration-300 shadow-lg">
                      <i className="ri-play-fill text-2xl text-background-50 ml-0.5" />
                    </div>
                  </div>
                  <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded text-[10px] font-medium bg-background-950/70 backdrop-blur-sm text-background-50">
                    {video.channel}
                  </div>
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <div className="flex items-center gap-2 text-xs text-foreground-500 mb-2">
                    <i className="ri-tv-line text-primary-500" />
                    {video.channel}
                    <span className="text-foreground-400">· {video.date}</span>
                  </div>
                  <h3 className="font-display font-semibold text-sm text-foreground-950 group-hover:text-primary-500 transition-colors leading-snug">
                    {video.title}
                  </h3>
                </div>
              </a>
            ))}
          </div>

          <div className="mt-10 text-center">
            <a
              href="https://www.youtube.com/user/maheshgovianu"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-primary-500/40 text-primary-600 font-semibold hover:bg-primary-500/10 transition-colors whitespace-nowrap"
            >
              <i className="ri-youtube-fill text-xl" />
              View All Videos on YouTube
              <i className="ri-arrow-right-up-line" />
            </a>
          </div>
        </div>
      </section>

      {/* Contact for Media */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-display font-bold text-foreground-950 mb-4">
            {mediaHeading}
          </h2>
            <p className="text-foreground-600 mb-8">
              For press inquiries, interview requests, or media partnerships, please contact our communications team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`mailto:${mediaEmail}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-mail-line" />
                {mediaEmail}
              </a>
              <a
                href={`tel:${phoneRaw}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-primary-500/40 text-primary-600 font-semibold hover:bg-primary-500/10 transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line" />
                {displayPhone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
            {ctaHeading}
          </h2>
          <p className="text-foreground-600 mb-8">
            Follow us for the latest news, insights, and success stories from the world of land monetization.
          </p>
          <div className="flex items-center justify-center gap-4">
            <a
              href="#"
              aria-label="Twitter"
              className="w-12 h-12 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 hover:bg-primary-500/20 hover:text-primary-500 transition-colors"
            >
              <i className="ri-twitter-x-line text-xl" />
            </a>
            <a
              href="#"
              aria-label="LinkedIn"
              className="w-12 h-12 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 hover:bg-primary-500/20 hover:text-primary-500 transition-colors"
            >
              <i className="ri-linkedin-box-line text-xl" />
            </a>
            <a
              href="#"
              aria-label="Instagram"
              className="w-12 h-12 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 hover:bg-primary-500/20 hover:text-primary-500 transition-colors"
            >
              <i className="ri-instagram-line text-xl" />
            </a>
            <a
              href="#"
              aria-label="YouTube"
              className="w-12 h-12 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 hover:bg-primary-500/20 hover:text-primary-500 transition-colors"
            >
              <i className="ri-youtube-line text-xl" />
            </a>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}